import get from 'lodash/get';
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Spinner from '../../../../components/LazyWidgets/Spinner';
import BigText from '../../../../components/LazyWidgets/BigText';
import {
  getStudentOverviewReportV2 as getStudentOverviewReportAction,
  sendAnalyticsEvent,
} from '../../../actions/reports';
import ReportError from '../ReportError';
import EmailWithTooltip from '../../../components/EmailTooltip/EmailWithTooltip';
import s from './quantQualStudentOverview.module.css';
import style from '../../../../components/UnitCards/UnitCards.module.css';
import { performanceByUnitComponent as PerformanceByUnitComponent, renderStudentOverviewHeroCard } from './qqComponents';
import { LEGEND_COLORS } from '../../reports-constants';
import StudentPerformanceByAssignmentComponent from '../QuantQualStudentUnit/studentPerformanceByAssignmentComponent/studentPerformanceByAssignmentComponent';
import LearningObjectives from '../../../../components/LearningObjectives/LearningObjectives';
import { NULL_STATE_TYPE } from '../../../actions/constants';
import ExternalFrameworkCard from '../../../../components/ExternalFrameworkCard';
import { getConfigObject } from '../../../util/config';

class QQStudentOverview extends Component {
  static propTypes = {
    reportType: PropTypes.string,
    onTabChange: PropTypes.func,
    studentId: PropTypes.string,
    reports: PropTypes.object,
    studentOverview: PropTypes.object,
    getStudentOverviewReport: PropTypes.func,
    loading: PropTypes.bool,
    error: PropTypes.any,
  };

  constructor(props) {
    super(props);

    this.getLearningObjectiveData = this.getLearningObjectiveData.bind(this);
  }

  componentDidMount() {
    const { reports: { courseAbstract: { id: courseId } }, studentId } = this.props;
    this.props.getStudentOverviewReport(courseId, studentId);
  }

  getBeautifyData(data) {
    return data.map(row => ({
      ...row,
      correctOnFirstTrySortValue: row.correctOnFirstTry,
      isNullState: !(row.correctOnFirstTry >= 0),
      correctOnFirstTry: row.correctOnFirstTry >= 0 ? `${Math.round(row.correctOnFirstTry * 100)}%` : '—',
      children: row.children.map(element => ({
        ...element,
        correctOnFirstTrySortValue: element.correctOnFirstTry,
        isNullState: !(element.correctOnFirstTry >= 0),
        correctOnFirstTry: element.correctOnFirstTry >= 0 ? `${Math.round(element.correctOnFirstTry * 100)}%` : '—',
        completedQuestions: element.completedQuestions >= 0 ? element.completedQuestions : '—',
      })),
    }));
  }

  getUnitCardContent(unit) {
    const getMetric = value => ({
      value: value > -1 ? `${Math.round(value * 100)}%` : '—',
      color: LEGEND_COLORS.BLACK,
    });

    const { value: completionValue, color: completionColor } = getMetric(unit.averageCompletion);
    const { value: proficiencyValue, color: proficiencyColor } = getMetric(unit.averageProficiency);
    return (
      <div className={style.scoreContainer}>
        {!unit.notStarted && <BigText className={style.bigText} color={completionColor} text={completionValue} alignment="center" />}
        {!unit.notStarted && <BigText className={style.bigText} color={proficiencyColor} text={proficiencyValue} alignment="center" />}
        {unit.notStarted && <BigText className={style.notStarted} text="Not Started" alignment="center" />}
        {!unit.notStarted && <div className={style.unitAvgScore}>on Completion Assignments</div>}
        {!unit.notStarted && <div className={style.unitAvgScore}>on Proficiency Assignments</div>}
      </div>
    );
  }

  getLearningObjectiveData(data, unitNotStarted, unitAssignments, reportType) {
    const learningObjectiveData = data && data.length !== 0 ? this.getBeautifyData(data) : [];
    const lOHeader = [
      {
        accessor: 'name',
        Header: 'Objective Title',
      },
      {
        accessor: 'startedQuestions',
        Header: 'Questions Started',
      },
      {
        accessor: 'completedQuestions',
        Header: 'Questions completed',
      },
      {
        accessor: 'correctOnFirstTry',
        sortValue: 'correctOnFirstTrySortValue',
        Header: 'Questions Correct On First Try',
      },
    ];

    const cellStyling = [
      {
        accessor: 'startedQuestions',
        alignment: 'right',
      },
      {
        accessor: 'completedQuestions',
        alignment: 'right',
      },
      {
        accessor: 'correctOnFirstTry',
        alignment: 'right',
      },
    ];

    const lOModal = {
      title: (<p>Student Performance by Learning Objective</p>),
      content: (
        <div>
          <p>
            <b>Questions started</b> represents the number of questions assessing this learning objective that this student began.<br />
          </p>
          <p>
            <b>Questions completed</b> represents the number of questions assessing this learning objective that your students completed.<br />
          </p>
          <p>
            <b>Questions correct on first try</b> represents, across all attempted questions assessing this learning objective, the proportion that were correct on this student’s first try.<br />
          </p>
        </div>
      ),
    };

    const subtitleTextFunc = (row, amount) => (
      <div>
        This student has scored below 60% on
        <b>{` ${row.lowScoring} of ${amount} `}</b>
        {`Learning Objective${amount > 1 ? 's' : ''}`}
      </div >
    );

    const nullStateMsg = this.getNullState(data, unitNotStarted, unitAssignments);

    return (
      <LearningObjectives
        data={data ? learningObjectiveData : []}
        onTabChange={this.props.onTabChange}
        accessors={lOHeader}
        modal={lOModal}
        nullStateMessage={nullStateMsg}
        getSubtitleLOText={subtitleTextFunc}
        primaryMeasureAccessor="correctOnFirstTry"
        cellStyling={cellStyling}
        className={s.studentLO}
        reportType={reportType}
      />);
  }

  getNullState(data, unitNotStarted, unitAssignments) {
    let nullStateType = null;
    if (unitAssignments === 0) {
      nullStateType = NULL_STATE_TYPE.noAssignments;
    } else if (unitNotStarted) {
      nullStateType = NULL_STATE_TYPE.noActivity;
    } else if (!data || !data.length) {
      nullStateType = NULL_STATE_TYPE.noQuestionsTagged;
    }

    if (!nullStateType) return null;

    return (
      <div className={s.qqLOTitleContainerNoText}>
        <p className={s.qqLONoDataText}>
          {{
            [NULL_STATE_TYPE.noAssignments]: <span>There are no assignments with Learning Objectives in this Unit.<br />
              Go to the <button role="link" className={s.qqLONoDataLink} onClick={() => { this.props.onTabChange('assignments'); }}>Course Planner</button> tab to setup and manage assignments.</span>,
            [NULL_STATE_TYPE.noActivity]: <span>After this student starts working on the assignments in this Unit, check here to see how they are performing against Learning Objectives.</span>,
            [NULL_STATE_TYPE.noQuestionsTagged]: <span>The questions started by this student do not cover any learning objectives. After this student starts working on learning objectives in this unit,
              check here to see the student&#39;s performance on those objectives.</span>,
          }[nullStateType]}
        </p>
      </div>);
  }

  render() {
    if (this.props.loading) return <Spinner className={s.loading} />;

    const { onTabChange, studentId, studentOverview, reportType, error } = this.props;
    const {
      activeUnit,
      performanceByAssignment,
      performanceByExternalFrameworks,
      performanceByLearningObjective,
      performanceByUnit = {},
      courseId,
    } = studentOverview;

    if (error || studentOverview.error) return <ReportError />;

    const ReportTitle = () => (
      <Fragment>
        <div className={s.studentTitle}>
          {studentOverview.student.name}
        </div>
        <EmailWithTooltip
          email={studentOverview.student.email}
          className={s.studentTitleEmail}
        />
      </Fragment>
    );

    return (
      <div className={s.reportContainer}>
        <ReportTitle />

        {renderStudentOverviewHeroCard({
          heroCardData: studentOverview.activeUnit || null,
          onTabChange,
          studentId,
        })}

        {performanceByUnit && (
          <PerformanceByUnitComponent
            data={performanceByUnit}
            onTabChange={onTabChange}
            studentId={studentId}
            reportType={reportType}
            sendEvent={sendAnalyticsEvent}
          />
        )}

        {performanceByAssignment && (
          <StudentPerformanceByAssignmentComponent
            data={performanceByAssignment}
            courseId={courseId}
            onTabChange={onTabChange}
            reportType={reportType}
            sendEvent={sendAnalyticsEvent}
          />
        )}

        {getConfigObject().FEATURE_USE_EXTERNAL_INSIGHTS && (
          <ExternalFrameworkCard
            reportType={reportType}
            rows={performanceByExternalFrameworks}
          />
        )}

        {this.getLearningObjectiveData(
          performanceByLearningObjective,
          get(activeUnit, "notStarted", false),
          get(activeUnit, "assignments", []),
          reportType
        )}
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    { 
      getStudentOverviewReport: getStudentOverviewReportAction,
      sendAnalyticsEvent,
    },
    dispatch
  );

function mapStateToProps(state) {
  return {
    reports: state.reports,
    studentOverview: state.reports.studentOverview,
    loading: !state.reports.courseAbstract || state.reports.courseAbstract.loading
      || !state.reports.studentOverview || state.reports.studentOverview.loading,
    error: state.reports.courseAbstract.error,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(QQStudentOverview);
